import React, { useEffect, useState } from "react";
import {
    FiSearch,
    FiRefreshCw,
    FiPrinter,
    FiPlus,
    FiCalendar,
    FiTrash2,
} from "react-icons/fi";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/api";
import Navbar from "../../components/Navbar";
import header from "../../assets/images/header.png";
// ─────────────────────────────────────────────────────────────────────────────
// BADGE CONFIG
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_LAYOUT = {
    logo: { visible: true, position: "top", size: 48, align: "left" },
    eventName: { visible: true, fontSize: 14, fontWeight: "extrabold", color: "#0f172a", align: "left" },
    tagline: { visible: true, text: "Badge d'Accès Officiel", fontSize: 9, color: "#64748b", opacity: 85 },
    name: { visible: true, fontSize: 14, fontWeight: "bold", color: "#ffffff" },
    email: { visible: true, fontSize: 10, color: "#ffffff" },
    cin: { visible: true, fontSize: 11, color: "#ffffff", prefix: "CIN. NO: " },
    qrCode: { visible: true, size: 90, border: true, borderColor: "#cbd5e1" },
    footer: { visible: true, text: "ACCESS ALL AREAS • 24 HOURS" },
};

const DEFAULT_TYPE_CONFIG = {
    builder: {
        label: "BUILDER",
        subLabel: "OFFICIAL BUILDER",
        bodyColor: "#111827",
        accentColor: "#1f2937",
    },
    full_access: {
        label: "STAFF",
        subLabel: "OFFICIAL STAFF",
        bodyColor: "#14532d",
        accentColor: "#166534",
    },
    contractor: {
        label: "CONTRACTOR",
        subLabel: "OFFICIAL CONTRACTOR",
        bodyColor: "#991b1b",
        accentColor: "#7f1d1d",
    },
};

const resolveDesignKey = (type) => {
    if (type === "build_up" || type === "show_days" || type === "teardown") return "builder";
    if (type === "full_access") return "full_access";
    if (type === "contractor") return "contractor";
    return "builder";
};

// ─── Replace the merge function in Badges.jsx ───
const merge = (target, source) => {
    if (!source) return target;
    const out = { ...target };
    for (const key of Object.keys(source)) {
        if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
            out[key] = merge(target[key] || {}, source[key]);
        } else if (source[key] !== undefined) {
            out[key] = source[key]; // ✅ allow null (e.g. image removed)
        }
    }
    return out;
};

// ─── Add DEFAULT_HEADER (matches BadgeCustomization.jsx) ───
const DEFAULT_HEADER = {
    image: null,
    imagePath: null,
    height: 90,
    overlay: false,
    overlayColor: "#000000",
    overlayOpacity: 40,
    custom: {
        bgColor: "#0f172a",
        bgColor2: "#1e3a5f",
        gradientDir: "to right",
        showLogo: true,
        logoSize: 44,
        showEventName: true,
        eventNameFontSize: 15,
        eventNameColor: "#ffffff",
        eventNameWeight: "bold",
        showTagline: false,
        taglineText: "Badge d'Accès Officiel",
        taglineFontSize: 9,
        taglineColor: "#94a3b8",
        layout: "logo-left",
        padding: 16,
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// BADGE CARD
// ─────────────────────────────────────────────────────────────────────────────

// ─── Add CustomHeaderView (same as BadgeCustomization.jsx) ───
const CustomHeaderView = ({ cfg, logoUrl, eventName, height }) => {
    const c = merge(DEFAULT_HEADER.custom, cfg || {});
    const bg = `linear-gradient(${c.gradientDir}, ${c.bgColor}, ${c.bgColor2})`;
    const pad = c.padding || 16;

    const LogoEl = logoUrl && c.showLogo
        ? <img src={logoUrl} alt="logo" style={{ height: c.logoSize || 44, objectFit: "contain", flexShrink: 0 }} />
        : null;

    const NameEl = c.showEventName
        ? (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                <div style={{
                    fontSize: c.eventNameFontSize || 15,
                    fontWeight: c.eventNameWeight === "bold" ? 700 : c.eventNameWeight === "extrabold" ? 800 : 600,
                    color: c.eventNameColor || "#ffffff",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                    {eventName || "Event Name"}
                </div>
                {c.showTagline && (
                    <div style={{ fontSize: c.taglineFontSize || 9, color: c.taglineColor || "#94a3b8", marginTop: 3, letterSpacing: 0.5 }}>
                        {c.taglineText || "Badge d'Accès Officiel"}
                    </div>
                )}
            </div>
        ) : null;

    const renderLayout = () => {
        switch (c.layout) {
            case "logo-left":
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {LogoEl}{NameEl}
                    </div>
                );
            case "logo-right":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {NameEl}{LogoEl}
                    </div>
                );
            case "logo-top":
                return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: `${pad / 2}px ${pad}px`, width: "100%", height: "100%" }}>
                        {LogoEl}
                        {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
                    </div>
                );
            case "logo-only":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                        {LogoEl || <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>No logo</span>}
                    </div>
                );
            case "text-only":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{ width: "100%", height: height || 90, background: bg, flexShrink: 0, display: "flex", alignItems: "center", overflow: "hidden" }}>
            {renderLayout()}
        </div>
    );
};

// ─── Updated BadgeCard ───
const BadgeCard = ({ badge, standiste, eventName, badgeConfig }) => {
    const designKey = resolveDesignKey(badge.type);

    const savedTypes = badgeConfig?.types || {};
    const savedTypeConfig = savedTypes[designKey] || savedTypes[badge.type] || {};
    const defaultConfig = DEFAULT_TYPE_CONFIG[designKey];

    const typeConfig = { ...defaultConfig, ...savedTypeConfig };

    const globalLayout = badgeConfig?.globalLayout
        ? merge(DEFAULT_LAYOUT, badgeConfig.globalLayout)
        : DEFAULT_LAYOUT;

    const layout = typeConfig.layout
        ? merge(globalLayout, typeConfig.layout)
        : globalLayout;

    const bodyColor = typeConfig.bodyColor || defaultConfig.bodyColor;
    const accentColor = typeConfig.accentColor || defaultConfig.accentColor;
    const label = typeConfig.label || defaultConfig.label;
    const subLabel = typeConfig.subLabel || defaultConfig.subLabel;
    const logoUrl = badgeConfig?.logo;
    const font = globalLayout.font || "ui-sans-serif, system-ui, sans-serif";

    // ✅ Read header from badgeConfig.header (saved by BadgeCustomization)
    const hdr = merge(DEFAULT_HEADER, badgeConfig?.header || {});
    const headerHeight = hdr.height || 90;
    const hasImage = !!hdr.image;

    const renderHeader = () => {
        if (hasImage) {
            return (
                <div style={{ flexShrink: 0, height: headerHeight, position: "relative", overflow: "hidden" }}>
                    <img
                        src={hdr.image}
                        alt="header"
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {hdr.overlay && (
                        <div style={{
                            position: "absolute", inset: 0,
                            backgroundColor: hdr.overlayColor || "#000000",
                            opacity: (hdr.overlayOpacity ?? 40) / 100,
                            pointerEvents: "none",
                        }} />
                    )}
                </div>
            );
        }
        // ✅ No image → use the custom builder, same as BadgeCustomization preview
        return (
            <CustomHeaderView
                cfg={hdr.custom}
                logoUrl={logoUrl}
                eventName={eventName}
                height={headerHeight}
            />
        );
    };

    return (
        <div style={{
            width: "100%", height: "100%", background: "#fff",
            display: "flex", flexDirection: "column", fontFamily: font,
        }}>
            {renderHeader()}

            <div style={{
                flex: 1, background: bodyColor,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "space-between",
                padding: "20px 18px 16px", position: "relative", overflow: "hidden",
            }}>
                <div style={{
                    position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
                    backgroundImage: "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
                    backgroundSize: "20px 20px",
                }} />

                {/* ROLE */}
                <div style={{ textAlign: "center", zIndex: 1 }}>
                    <div style={{ fontSize: 9, color: "#fff", opacity: 0.55, letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>
                        {subLabel}
                    </div>
                    <div style={{ fontSize: label.length > 9 ? 26 : 34, fontWeight: 900, color: "#fff", letterSpacing: 0.5, lineHeight: 1.05, textTransform: "uppercase", textAlign: "center" }}>
                        {label}
                    </div>
                </div>

                {/* QR + CIN */}
                <div style={{ textAlign: "center", zIndex: 1, width: "100%" }}>
                    {layout.cin?.visible && standiste?.cin && (
                        <div style={{ fontSize: layout.cin?.fontSize || 11, color: layout.cin?.color || "#ffffff", opacity: 0.8, marginBottom: 8, letterSpacing: 1, fontWeight: 600 }}>
                            {layout.cin?.prefix || "CIN. NO: "}
                            <span style={{ fontFamily: "monospace" }}>{standiste.cin}</span>
                        </div>
                    )}
                    {layout.qrCode?.visible && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <div style={{ background: "#fff", padding: 8, border: `3px solid ${accentColor}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                                <QRCode value={badge._id} size={layout.qrCode?.size || 90} />
                            </div>
                        </div>
                    )}
                </div>

                {/* NAME / EMAIL */}
                <div style={{ textAlign: "center", zIndex: 1 }}>
                    {layout.name?.visible && (
                        <div style={{ fontSize: layout.name?.fontSize || 14, fontWeight: 700, color: layout.name?.color || "#ffffff", marginBottom: 2 }}>
                            {standiste?.name}
                        </div>
                    )}
                    {layout.email?.visible && standiste?.email && (
                        <div style={{ fontSize: layout.email?.fontSize || 10, color: layout.email?.color || "#ffffff", opacity: 0.65 }}>
                            {standiste.email}
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                {layout.footer?.visible && (
                    <div style={{ zIndex: 1, width: "100%", borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 10, textAlign: "center" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: 1.5, textTransform: "uppercase" }}>
                            {layout.footer?.text || "ACCESS ALL AREAS"}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

const Badges = () => {
    const [badges, setBadges] = useState([]);
    const [filteredBadges, setFilteredBadges] = useState([]);
    const [eventBadgeConfigs, setEventBadgeConfigs] = useState({});
    const [loading, setLoading] = useState(true);

    const [printBadge, setPrintBadge] = useState(null);
    const [saving, setSaving] = useState(false);
    const [filters, setFilters] = useState({
        search: "",
        startDate: "",
        endDate: "",
    });

    // FETCH

    const fetchBadges = async () => {
        try {
            setLoading(true);

            const userId = localStorage.getItem("userId");

            const { data } = await axiosInstance.get(`/badge/by-chef/${userId}`);

            const allBadges = data?.badges || [];

            setBadges(allBadges);
            setFilteredBadges(allBadges);

            // Collect unique event IDs and fetch their badgeConfig
            const eventIds = [
                ...new Set(
                    allBadges
                        .map((b) => {
                            const eid = b.standiste?.event || b.chef?.event || b.event;
                            return eid?._id || eid; // normalize populated objects
                        })
                        .filter(Boolean)
                ),
            ];

            if (eventIds.length > 0) {
                fetchEventBadgeConfigs(eventIds);
            }
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des badges");
        } finally {
            setLoading(false);
        }
    };

    const fetchEventBadgeConfigs = async (eventIds) => {
        const results = await Promise.allSettled(
            eventIds.map((eid) => axiosInstance.get(`/event/${eid}`))
        );
        const map = {};
        results.forEach((r, i) => {
            if (r.status === "fulfilled") {
                map[eventIds[i]] = r.value.data?.event?.badgeConfig || null;
            }
        });
        setEventBadgeConfigs(map);
    };

    useEffect(() => {
        fetchBadges();
    }, []);

    // FILTERS

    useEffect(() => {
        let result = [...badges];

        if (filters.search) {
            const q = filters.search.toLowerCase();

            result = result.filter((b) =>
                [b?.name, b?.cin]
                    .join(" ")
                    .toLowerCase()
                    .includes(q)
            );
        }

        if (filters.startDate) {
            result = result.filter(
                (b) =>
                    new Date(b.createdAt) >=
                    new Date(filters.startDate)
            );
        }

        if (filters.endDate) {
            result = result.filter(
                (b) =>
                    new Date(b.createdAt) <=
                    new Date(filters.endDate)
            );
        }

        setFilteredBadges(result);
    }, [filters, badges]);

    const resetFilters = () => {
        setFilters({
            search: "",
            startDate: "",
            endDate: "",
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce badge ?")) return;

        try {
            await axiosInstance.delete(`/badge/${id}`);

            toast.success("Badge supprimé");

            fetchBadges();
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                "Erreur suppression"
            );
        }
    };

    // PRINT

    const handlePrintBadge = (badge) => {
        if (badge.status !== "active") {
            toast.warn("Ce badge est inactif et ne peut pas être imprimé");
            return;
        }
        const rawEventId = badge.standiste?.event || badge.chef?.event || badge.event;
        const eventId = rawEventId?._id || rawEventId;

        const badgeConfig =
            badge.badgeConfig ||
            (eventId ? eventBadgeConfigs[eventId] : null) ||
            null;

        const person = {
            name: badge.name || badge.standiste?.name,
            cin: badge.cin || badge.standiste?.cin,
            email: badge.email || badge.standiste?.email,
            cinPhotos: badge.cinPhotos,
        };

        setPrintBadge({ badge, person, badgeConfig });

        setTimeout(() => {
            window.print();
        }, 150);
    };

    const handleTypeChange = async (badge, newType) => {
        if (newType === badge.type) return;
        try {
            await axiosInstance.put(`/badge/${badge._id}`, {
                type: newType,
            });
            toast.success("Type mis à jour");
            fetchBadges();
        } catch (err) {
            toast.error(err?.response?.data?.message || "Erreur mise à jour du type");
        }
    };

    const TYPE_OPTIONS_BY_DESIGN = {
        builder: [
            { value: "build_up", label: "Build Up" },
            { value: "show_days", label: "Show Days" },
            { value: "teardown", label: "Teardown" },
        ],
        full_access: [{ value: "full_access", label: "Full Access (Staff)" }],
        contractor: [{ value: "contractor", label: "Contractor" }],
    };

    const handleMobilePrintTap = () => {
        toast.info("L'impression n'est disponible que depuis un ordinateur (PC/Mac). Merci d'utiliser un laptop pour imprimer ce badge.");
    };

    useEffect(() => {
        const afterPrint = () => {
            setPrintBadge(null);
        };

        window.addEventListener("afterprint", afterPrint);

        return () => {
            window.removeEventListener(
                "afterprint",
                afterPrint
            );
        };
    }, []);

    // HELPERS

    const formatDate = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleDateString("fr-FR");
    };

    // RENDER

    return (
        <>
            <style>{`
            @media print {

                html,
                body {
                    width: 85.6mm !important;
                    height: 135mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    background: white !important;
                }

                body {
                    visibility: hidden !important;
                }

                #print-badge-page,
                #print-badge-page * {
                    visibility: visible !important;
                }

                #print-badge-page {
                    position: fixed !important;
                    inset: 0 !important;
                    width: 85.6mm !important;
                    height: 135mm !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    overflow: hidden !important;
                    z-index: 999999 !important;
                    background: white !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }

                #print-badge-page > div {
                    width: 85.6mm !important;
                    height: 135mm !important;
                }

                @page {
                    size: 85.6mm 135mm !important;
                    margin: 0 !important;
                }
            }
        `}</style>

            {printBadge && (
                <div id="print-badge-page" className="hidden">
                    <div
                        style={{
                            width: "85.6mm",
                            height: "135mm",
                        }}
                    >
                        <BadgeCard
                            badge={printBadge.badge}
                            standiste={printBadge.person}
                            eventName={
                                printBadge.badge?.eventName ||
                                printBadge.badge?.event?.name ||
                                ""
                            }
                            badgeConfig={printBadge.badgeConfig}
                        />
                    </div>
                </div>
            )}

            <Navbar />

            <div className="min-h-screen bg-[#f5f3ee] px-3 py-5 sm:px-6 sm:py-8">
                <div className="max-w-7xl mx-auto">

                    {/* HEADER */}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">
                                Mes Badges
                            </h1>

                            <p className="text-sm text-slate-500 mt-1">
                                {filteredBadges.length} badge(s)
                                au total
                            </p>
                        </div>

                        <a
                            href="/badges/create"
                            className="h-11 px-5 rounded-2xl bg-blue-900 hover:bg-blue-800 text-white flex items-center justify-center gap-2 font-medium text-sm shadow-lg shadow-blue-900/20"
                        >
                            <FiPlus size={16} />
                            Nouveau badge
                        </a>
                    </div>

                    {/* FILTERS */}

                    <div className="bg-white rounded-3xl border border-slate-200 p-4 sm:p-5 shadow-sm mb-5">
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px_180px_auto] gap-3">

                            <div className="relative">
                                <FiSearch
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={16}
                                />

                                <input
                                    type="text"
                                    placeholder="Rechercher (nom, CIN...)"
                                    value={filters.search}
                                    onChange={(e) =>
                                        setFilters((p) => ({
                                            ...p,
                                            search:
                                                e.target.value,
                                        }))
                                    }
                                    className="w-full h-12 rounded-2xl border border-slate-300 bg-white pl-11 pr-4 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100 transition"
                                />
                            </div>

                            <div className="relative">
                                <FiCalendar
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={15}
                                />

                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) =>
                                        setFilters((p) => ({
                                            ...p,
                                            startDate:
                                                e.target.value,
                                        }))
                                    }
                                    className="w-full h-12 rounded-2xl border border-slate-300 bg-white pl-11 pr-4 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100 transition"
                                />
                            </div>

                            <div className="relative">
                                <FiCalendar
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                                    size={15}
                                />

                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) =>
                                        setFilters((p) => ({
                                            ...p,
                                            endDate:
                                                e.target.value,
                                        }))
                                    }
                                    className="w-full h-12 rounded-2xl border border-slate-300 bg-white pl-11 pr-4 text-sm outline-none focus:border-blue-900 focus:ring-4 focus:ring-blue-100 transition"
                                />
                            </div>

                            <button
                                onClick={resetFilters}
                                className="h-12 px-4 rounded-2xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium flex items-center justify-center gap-2 transition"
                            >
                                <FiRefreshCw size={15} />
                                Réinitialiser
                            </button>
                        </div>
                    </div>

                    {/* TABLE */}

                    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">

                        {/* DESKTOP */}

                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full min-w-[1000px]">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        {[
                                            "CIN Photo",
                                            "Identité",
                                            "CIN/Passeport",
                                            "Type",
                                            "Statut",
                                            "Créé le",
                                            "Actions",
                                        ].map((title) => (
                                            <th
                                                key={title}
                                                className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                                            >
                                                {title}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="py-20 text-center text-slate-400"
                                            >
                                                Chargement...
                                            </td>
                                        </tr>
                                    ) : filteredBadges.length ===
                                        0 ? (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="py-20 text-center text-slate-400"
                                            >
                                                Aucun badge trouvé
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredBadges.map(
                                            (badge) => (
                                                <tr
                                                    key={
                                                        badge._id
                                                    }
                                                    className="border-b border-slate-100 hover:bg-slate-50 transition"
                                                >
                                                    <td className="px-5 py-4">
                                                        {(() => {
                                                            const photos =
                                                                badge.cinPhotos?.length > 0
                                                                    ? badge.cinPhotos
                                                                    : badge.standiste?.cinPhotos || [];

                                                            return (
                                                                <div className="flex gap-2">
                                                                    {[0, 1].map((i) => (
                                                                        <button
                                                                            key={i}
                                                                            className="w-14 h-14 rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0"
                                                                            title="Voir les détails"
                                                                        >
                                                                            {photos[i] ? (
                                                                                <img
                                                                                    src={photos[i]}
                                                                                    alt=""
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center text-[9px] text-slate-300">
                                                                                    —
                                                                                </div>
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>

                                                    <td className="px-5 py-4">
                                                        <p className="text-sm font-semibold text-slate-800">
                                                            {
                                                                badge.name || badge.standiste?.name
                                                            }
                                                        </p>

                                                        <p className="text-xs text-slate-400 mt-1 uppercase">
                                                            {
                                                                badge.type
                                                            }
                                                        </p>
                                                    </td>

                                                    <td className="px-5 py-4 text-sm text-slate-700 font-medium">
                                                        {badge.cin || badge.standiste?.cin || "-"}
                                                    </td>

                                                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                                                        <select
                                                            value={badge.type}
                                                            onChange={(e) => handleTypeChange(badge, e.target.value)}
                                                            className="px-2.5 py-1.5 rounded-xl bg-blue-50 text-blue-900 text-xs font-semibold capitalize border-none outline-none focus:ring-2 focus:ring-blue-200 cursor-pointer"
                                                        >
                                                            {TYPE_OPTIONS_BY_DESIGN[resolveDesignKey(badge.type)].map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>

                                                    <td className="px-5 py-4">
                                                        <span
                                                            className={`inline-flex px-3 py-1 rounded-xl text-xs font-semibold ${badge.status === "active"
                                                                ? "bg-green-50 text-green-700"
                                                                : "bg-slate-100 text-slate-500"
                                                                }`}
                                                        >
                                                            {badge.status === "active" ? "Actif" : "Inactif"}
                                                        </span>
                                                    </td>

                                                    <td className="px-5 py-4 text-sm text-slate-600">
                                                        {formatDate(
                                                            badge.createdAt
                                                        )}
                                                    </td>

                                                    <td className="px-5 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handlePrintBadge(badge)}
                                                                disabled={badge.status !== "active"}
                                                                title={badge.status !== "active" ? "Badge inactif — impression désactivée" : "Imprimer"}
                                                                className={`h-9 px-3 rounded-xl border text-xs font-medium flex items-center gap-1.5 transition ${badge.status !== "active"
                                                                    ? "border-slate-200 text-slate-300 cursor-not-allowed"
                                                                    : "border-blue-200 text-blue-900 hover:bg-blue-50"
                                                                    }`}
                                                            >
                                                                <FiPrinter size={13} />
                                                                Imprimer
                                                            </button>

                                                            <button
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        badge._id
                                                                    )
                                                                }
                                                                className="h-9 px-3 rounded-xl border border-red-200 text-red-500 hover:bg-red-50 text-xs font-medium flex items-center gap-1.5 transition"
                                                            >
                                                                <FiTrash2
                                                                    size={
                                                                        13
                                                                    }
                                                                />
                                                                Supprimer
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBILE */}

                        <div className="lg:hidden p-3 space-y-2.5">
                            {loading ? (
                                <div className="py-16 text-center text-slate-400 text-sm">
                                    Chargement...
                                </div>
                            ) : filteredBadges.length === 0 ? (
                                <div className="py-16 text-center text-slate-400 text-sm">
                                    Aucun badge trouvé
                                </div>
                            ) : (
                                filteredBadges.map((badge) => (
                                    <div
                                        key={badge._id}
                                        className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                                    >
                                        <div className="w-full flex gap-3 p-3 text-left">
                                            <div className="flex -space-x-3 shrink-0">
                                                {(() => {
                                                    const photos =
                                                        badge.cinPhotos?.length > 0
                                                            ? badge.cinPhotos
                                                            : badge.standiste?.cinPhotos || [];

                                                    return [0, 1].map((i) => (
                                                        <div
                                                            key={i}
                                                            className="w-14 h-14 rounded-xl overflow-hidden border-2 border-white shadow-sm bg-slate-100 ring-1 ring-slate-200"
                                                            style={{ zIndex: 2 - i }}
                                                        >
                                                            {photos[i] ? (
                                                                <img
                                                                    src={photos[i]}
                                                                    alt=""
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-[9px] text-slate-300">
                                                                    —
                                                                </div>
                                                            )}
                                                        </div>
                                                    ));
                                                })()}
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-sm font-bold text-slate-800 truncate">
                                                        {badge.name || badge.standiste?.name}
                                                    </h3>
                                                    <span
                                                        className={`shrink-0 w-1.5 h-1.5 rounded-full ${badge.status === "active" ? "bg-green-500" : "bg-slate-300"
                                                            }`}
                                                    />
                                                </div>

                                                <p className="text-xs text-slate-400 mt-0.5 truncate">
                                                    {badge.cin || badge.standiste?.cin || "-"}
                                                </p>

                                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                    <select
                                                        value={badge.type}
                                                        onChange={(e) => handleTypeChange(badge, e.target.value)}
                                                        className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-900 text-[10px] font-semibold uppercase border-none outline-none"
                                                    >
                                                        {TYPE_OPTIONS_BY_DESIGN[resolveDesignKey(badge.type)].map((opt) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <span
                                                        className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${badge.status === "active"
                                                            ? "bg-green-50 text-green-700"
                                                            : "bg-slate-100 text-slate-500"
                                                            }`}
                                                    >
                                                        {badge.status === "active" ? "Actif" : "Inactif"}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 ml-auto shrink-0">
                                                        {formatDate(badge.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex border-t border-slate-100">
                                            <button
                                                onClick={handleMobilePrintTap}
                                                className="flex-1 h-10 text-xs font-medium flex items-center justify-center gap-1.5 text-slate-400 active:bg-slate-50 transition"
                                            >
                                                <FiPrinter size={13} />
                                                PC requis
                                            </button>
                                            <div className="w-px bg-slate-100" />
                                            <button
                                                onClick={() => handleDelete(badge._id)}
                                                className="flex-1 h-10 text-xs font-medium flex items-center justify-center gap-1.5 text-red-500 active:bg-red-50 transition"
                                            >
                                                <FiTrash2 size={13} />
                                                Supprimer
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Badges;